/**
 * El lienzo del editor → un PNG que se le puede mandar a un motor de render.
 *
 * Es el puente del botón «Generar planta render»: lo que el motor ve es ESTA
 * imagen, así que lo que acá salga mal no lo arregla ningún prompt.
 *
 * Cuatro decisiones, y las cuatro son por una trampa medida:
 *
 *  1. **Se rasteriza la planta ENTERA, no lo que está en pantalla.** El
 *     `viewBox` del lienzo vivo es el encuadre del momento —zoom y paneo del
 *     usuario—, así que serializarlo tal cual mandaba media casa recortada y el
 *     motor «completaba» la mitad que faltaba. El `viewBox` se recalcula con
 *     `getBBox()`, que da la caja de todo el contenido en unidades de usuario.
 *
 *  2. **Se clona el nodo.** Tocarle el `viewBox` al `<svg>` vivo lo repinta en
 *     pantalla: el usuario vería el plano saltar mientras se genera.
 *
 *  3. **Nada de `fetch` ni de URLs externas adentro.** El plano de fondo ya
 *     entra como `data:` (`EditorPlanta` lo pasa por `canvas.toDataURL`), que es
 *     lo único que sobrevive al viaje: una imagen por URL deja el canvas
 *     *tainted* y `toBlob` tira `SecurityError`. Si algún día el fondo pasa a ser
 *     una URL, acá hay que embeberla antes.
 *
 *  4. **El tamaño lo manda el lado largo, no un ancho fijo.** Una casa de lote
 *     angosto es muy vertical; fijar el ancho la deja con 300 px de alto útil y
 *     el motor no distingue un tabique de una junta.
 */

/** Lado largo del PNG que se manda. Más no agrega: el motor reescala a 1536. */
const LADO_LARGO = 1536;

/**
 * Margen en unidades de usuario alrededor del contenido.
 *
 * No es estética: sin aire, la pared del perímetro queda pegada al borde y los
 * motores de difusión tratan el borde de la imagen como «acá se corta la
 * escena», así que inventan terreno afuera para rellenar.
 */
const AIRE = 0.4;

export type PlantaRaster = {
  png: Blob;
  ancho: number;
  alto: number;
};

/**
 * Una superficie pintada debajo de todo, en metros.
 *
 * `rasterizar` no sabe de recintos ni de clases a propósito: recibe formas con
 * su color ya resuelto. Quien las arma es `EditorPlanta`, que es el único que
 * conoce la medición.
 */
export type Mancha = { celdas: [number, number, number, number][]; color: string };

/**
 * El color del suelo cuando la planta no trae un área de lote dibujada.
 *
 * No es decoración: es un LÍMITE. Medido el 14-oct-2026 — con la base terminando
 * en la pared del perímetro, `gpt-image-1` dibujó una cochera con dos autos, una
 * acera y franjas de césped que no estaban en ningún lado, copiadas tal cual de
 * la imagen de referencia. El prompt dice «no inventar accesos ni
 * estacionamientos» en la sección 5 y lo ignora, porque una imagen pesa más que
 * un párrafo. Darle suelo hasta el borde es decirle dónde se acaba el mundo.
 */
const SUELO = '#9fae86';

/**
 * Toma el lienzo del editor y devuelve su PNG.
 *
 * Busca por `data-lienzo` y no por `querySelector('svg')` por la misma razón que
 * documenta `EditorPlanta`: cada ícono de Phosphor también es un `<svg>`, y el
 * día que uno subió a la barra de arriba el que caía primero por orden del DOM
 * dejó de ser el plano.
 */
export async function rasterizarLienzo(manchas: Mancha[] = []): Promise<PlantaRaster> {
  const vivo = document.querySelector<SVGSVGElement>('svg[data-lienzo]');
  if (!vivo) throw new Error('No encontré el lienzo de la planta.');

  // `getBBox` mide el contenido, no el encuadre. Un lienzo en blanco da 0 y
  // conviene decirlo antes de gastar un render en una hoja vacía.
  const caja = vivo.getBBox();
  if (!caja.width || !caja.height) {
    throw new Error('La planta está vacía: no hay nada que renderizar.');
  }

  const vbAncho = caja.width + AIRE * 2;
  const vbAlto = caja.height + AIRE * 2;

  const escala = LADO_LARGO / Math.max(vbAncho, vbAlto);
  const ancho = Math.round(vbAncho * escala);
  const alto = Math.round(vbAlto * escala);

  const clon = vivo.cloneNode(true) as SVGSVGElement;
  clon.setAttribute('viewBox', `${caja.x - AIRE} ${caja.y - AIRE} ${vbAncho} ${vbAlto}`);
  clon.setAttribute('width', String(ancho));
  clon.setAttribute('height', String(alto));
  // El lienzo vivo hereda el blanco de la página; serializado, el fondo queda
  // transparente y al aplanarlo contra negro la planta sale en negativo.
  clon.setAttribute('style', 'background:#ffffff');
  // Sin el namespace explícito el markup serializado no es un SVG válido para
  // `<img>` y la carga falla sin decir por qué.
  clon.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

  // El croquis de fondo NO viaja. Es un PDF con cotas, rótulos y nombres de
  // cuarto, y la sección 1 del prompt pide justamente eliminarlos: mandarlo es
  // pedirle al motor que borre lo que uno mismo le acaba de poner. Además queda
  // debajo del suelo que se pinta acá abajo, así que tampoco se vería.
  clon.querySelectorAll('image').forEach((i) => i.remove());

  // EL SUELO, debajo de todo. Sin esto el motor recibe un dibujo de líneas
  // flotando en blanco y completa el terreno copiándolo de la referencia.
  const ns = 'http://www.w3.org/2000/svg';
  const piso = document.createElementNS(ns, 'g');
  const caer = (x: number, y: number, w: number, h: number, color: string) => {
    const r = document.createElementNS(ns, 'rect');
    r.setAttribute('x', String(x)); r.setAttribute('y', String(y));
    r.setAttribute('width', String(w)); r.setAttribute('height', String(h));
    r.setAttribute('fill', color);
    // Las celdas de una región se tocan de canto. Sin el solape de medio
    // milímetro el antialias deja una rejilla de rayas claras entre ellas, y el
    // motor lee esas rayas como juntas de piso y las dibuja gigantes.
    r.setAttribute('shape-rendering', 'crispEdges');
    piso.appendChild(r);
  };
  caer(caja.x - AIRE, caja.y - AIRE, vbAncho, vbAlto, SUELO);
  for (const m of manchas) {
    for (const [x, y, w, h] of m.celdas) caer(x, y, w + 0.002, h + 0.002, m.color);
  }
  clon.insertBefore(piso, clon.firstChild);

  const markup = new XMLSerializer().serializeToString(clon);
  const url = URL.createObjectURL(new Blob([markup], { type: 'image/svg+xml;charset=utf-8' }));

  try {
    const img = await cargar(url);
    const cv = document.createElement('canvas');
    cv.width = ancho;
    cv.height = alto;
    const ctx = cv.getContext('2d');
    if (!ctx) throw new Error('El navegador no dio contexto 2D.');

    // El blanco se pinta a mano: el `background` del SVG no viaja al canvas.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, ancho, alto);
    ctx.drawImage(img, 0, 0, ancho, alto);

    const png = await new Promise<Blob | null>((ok) => cv.toBlob(ok, 'image/png'));
    if (!png) throw new Error('No se pudo armar el PNG de la planta.');

    return { png, ancho, alto };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function cargar(url: string): Promise<HTMLImageElement> {
  return new Promise((ok, falla) => {
    const img = new Image();
    img.onload = () => ok(img);
    // El error de `<img>` no trae motivo. El más común de lejos es markup que
    // el parser de SVG rechaza, así que vale decirlo en vez de «error».
    img.onerror = () => falla(new Error('El navegador no pudo leer el dibujo de la planta.'));
    img.src = url;
  });
}
